/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useFloating, autoUpdate, offset, flip, shift, useHover, useFocus, useDismiss, useInteractions, safePolygon, size, autoPlacement, hide, inline, useDelayGroupContext, useDelayGroup, } from '@floating-ui/react';
import { Box, Portal, cloneElementWithRef, useControlledState, useId, useMergedRefs, } from '../../utils/index.js';
const useTooltip = (options = {}) => {
    const uniqueId = useId();
    const { placement = 'top', visible, onVisibleChange, middleware = { flip: true, shift: true }, autoUpdateOptions = {}, reference, ariaStrategy = 'description', id = uniqueId, ...props } = options;
    const [open, onOpenChange] = useControlledState(false, visible, onVisibleChange);
    const floating = useFloating({
        placement,
        open,
        onOpenChange,
        whileElementsMounted: React.useMemo(() => 
        // autoUpdate is expensive and should only be called when tooltip is open
        open ? (...args) => autoUpdate(...args, autoUpdateOptions) : undefined, [autoUpdateOptions, open]),
        middleware: React.useMemo(() => [
            middleware.offset !== undefined
                ? offset(middleware.offset)
                : offset(4),
            middleware.flip && flip(),
            middleware.shift && shift(),
            middleware.size && size(),
            middleware.autoPlacement && autoPlacement(),
            middleware.inline && inline(),
            middleware.hide && hide(),
        ].filter(Boolean), [middleware]),
        ...(reference && { elements: { reference } }),
    });
    const ariaProps = React.useMemo(() => ariaStrategy === 'description'
        ? { 'aria-describedby': id }
        : ariaStrategy === 'label'
            ? { 'aria-labelledby': id }
            : {}, [ariaStrategy, id]);
    const { delay } = useDelayGroupContext();
    useDelayGroup(floating.context, { id: useId() });
    const interactions = useInteractions([
        useHover(floating.context, {
            delay: delay ?? { open: 50, close: 250 },
            handleClose: safePolygon({ buffer: -Infinity }),
            move: false,
        }),
        useFocus(floating.context),
        useDismiss(floating.context, {
            referencePress: true,
            referencePressEvent: 'click',
        }),
    ]);
    // Manually add attributes and event handlers to external reference element,
    // because we cannot spread getReferenceProps onto it.
    React.useEffect(() => {
        if (!reference) {
            return;
        }
        /** e.g. onPointerDown --> pointerdown */
        const domEventName = (e) => e.toLowerCase().substring(2);
        const cleanupValues = {};
        Object.entries({
            ...ariaProps,
            ...interactions.getReferenceProps(),
        }).forEach(([key, value]) => {
            if (typeof value === 'function') {
                // manually add `.nativeEvent` (react concept) because floating-ui needs it
                const patchedHandler = (event) => {
                    value({ ...event, nativeEvent: event });
                };
                reference.addEventListener(domEventName(key), patchedHandler);
                cleanupValues[key] = patchedHandler;
            }
            else if (value) {
                cleanupValues[key] = reference.getAttribute(key);
                reference.setAttribute(key, value);
            }
        });
        return () => {
            Object.entries(cleanupValues).forEach(([key, value]) => {
                if (typeof value === 'function') {
                    reference.removeEventListener(domEventName(key), value);
                }
                else if (value) {
                    reference.setAttribute(key, value);
                }
                else {
                    reference.removeAttribute(key);
                }
            });
        };
    }, [ariaProps, reference, interactions]);
    const getReferenceProps = React.useCallback((userProps) => {
        return interactions.getReferenceProps({ ...userProps, ...ariaProps });
    }, [interactions, ariaProps]);
    const floatingProps = React.useMemo(() => interactions.getFloatingProps({
        hidden: !open,
        'aria-hidden': 'true',
        ...props,
        id,
    }), [interactions, props, id, open]);
    return React.useMemo(() => ({
        getReferenceProps,
        floatingProps,
        ...floating,
        // styles are not relevant when tooltip is not open
        floatingStyles: floating.context.open ? floating.floatingStyles : {},
    }), [getReferenceProps, floatingProps, floating]);
};
/**
 * Basic tooltip component to display informative content when an element is hovered or focused.
 * Uses [FloatingUI](https://floating-ui.com/).
 * @example
 * <Tooltip content='tooltip text' placement='top'>Hover here</Tooltip>
 * @example
 * const [trigger, setTrigger] = React.useState(null);
 * ...
 * <Button ref={setTrigger} />
 * <Tooltip content='tooltip text' reference={trigger} />
 */
export const Tooltip = React.forwardRef((props, forwardedRef) => {
    const { content, children, portal = true, className, style, ...rest } = props;
    const tooltip = useTooltip(rest);
    const refs = useMergedRefs(tooltip.refs.setFloating, forwardedRef);
    return (React.createElement(React.Fragment, null,
        cloneElementWithRef(children, (children) => ({
            ...tooltip.getReferenceProps(children.props),
            ref: tooltip.refs.setReference,
        })),
        // Tooltip must always be present in the DOM (even when closed) for ARIA to work
        props.ariaStrategy !== 'none' || tooltip.context.open ? (React.createElement(Portal, { portal: portal },
            React.createElement(Box, { className: cx('iui-tooltip', className), ref: refs, style: { ...tooltip.floatingStyles, ...style }, ...tooltip.floatingProps }, content))) : null));
});
